import { AsyncPipe, NgClass, NgStyle } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    inject,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { CmsService } from '@services/cms.service';

import { MixedCartMobileCms } from '@interfaces/directus-cms.interface';

import { ACTION_CLICK } from '@shared/tag/tag.interface';
import { TagService } from '@shared/tag/tag.service';

import { Observable, Subject } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';

import { AppService } from '../../../../../app/services/app.services';
import { DataCyDirective } from '../../../../common/directives/data-cy.directive';
import { DefaultBlocComponent } from './default-bloc/default-bloc.component';

@Component({
    selector: 'checkout-select-choice',
    templateUrl: './select-choice.component.html',
    styleUrl: 'select-choice.component.scss',
    changeDetection: ChangeDetectionStrategy.Default,
    standalone: true,
    imports: [ReactiveFormsModule, NgClass, NgStyle, DataCyDirective, AsyncPipe, DefaultBlocComponent],
})
export class SelectChoiceComponent implements OnInit, OnDestroy {
    @Input() public cartForm: FormGroup;
    @Input() public defaultValue: boolean = true;
    @Input() public isMobile: boolean;
    @Input() public mobileBloc: boolean = false;
    @Input() public isCompatiblePromoFoyer: boolean = true;
    @Input() public isFaiPureFiber: boolean = false;
    @Output() public onChoice: EventEmitter<boolean> = new EventEmitter<boolean>();

    public cmsData$: Observable<MixedCartMobileCms>;

    public selectChoiceForm: FormGroup<{
        choiceControl: FormControl<boolean>;
    }>;
    public waitForCartModifications: boolean = false;
    public suggestedProductLabel: string = '';
    public backgroundImgUrl: string = '';
    public backgroundKv: string = '';
    public styleOutterDiv: string = '';
    public fallbackBackgroundUrl = '';

    protected tagService: TagService = inject(TagService);
    protected cmsService: CmsService = inject(CmsService);
    protected formBuilder: FormBuilder = inject(FormBuilder);
    protected appService: AppService = inject(AppService);

    private destroy$: Subject<boolean> = new Subject<boolean>();

    public ngOnInit(): void {
        this.selectChoiceForm = this.formBuilder.group({
            choiceControl: [this.defaultValue, Validators.required],
        });
        this.sendPrintTags();
        this.suggestedProductLabel = this.mobileBloc ? 'un forfait mobile' : 'une box fibre';

        this.fallbackBackgroundUrl = `https://www.bouyguestelecom.fr/static/wbm/media/banners/${this.isCompatiblePromoFoyer ? 'pc10-fond-beige-240923' : 'kv-grey'}-background-lame_desktop.webp`;

        this.cmsData$ = this.cmsService.getMixedCartMobileData(0, this.isCompatiblePromoFoyer).pipe(
            tap((data) => {
                this.cartForm.addControl('mixed-choice', this.selectChoiceForm);
                this.selectChoiceForm
                    .get('choiceControl')
                    .valueChanges.pipe(
                        takeUntil(this.destroy$),
                        tap((value) => {
                            if (!value) {
                                this.cartForm.get('mixed-choice').get('choiceControl').setErrors({ invalid: false });
                            }
                            this.sendClickTags(value);
                            this.onChoice.next(value);
                        }),
                        map(() => data),
                    )
                    .subscribe();

                this.backgroundImgUrl = data.backgroundImg;
                this.backgroundKv = data.backgroundKv;
                this.populateBackgroundStyle();
            }),
        );
        this.populateBackgroundStyle();
    }

    ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

    private sendPrintTags(): void {
        this.tagService.sendEvent(
            'Affichage_banniere_panier_mixte',
            `Banniere_panier_mixte_${this.mobileBloc ? 'forfait' : 'box'}`,
            ACTION_CLICK.PRINTING,
        );
    }

    private sendClickTags(value: boolean): void {
        this.tagService.sendEvent(
            value ? 'Oui' : 'Non',
            `Banniere_panier_mixte_${this.mobileBloc ? 'forfait' : 'box'}`,
            ACTION_CLICK.CLICK,
        );
    }

    private populateBackgroundStyle(): void {
        const background = this.backgroundKv
            ? `https://www.bouyguestelecom.fr/static/wbm/media/banners/${this.backgroundKv}-background-lame_desktop.webp`
            : this.backgroundImgUrl;
        const backgroundImage = this.isFaiPureFiber ? 'unset' : `url(${background || this.fallbackBackgroundUrl})`;
        // background color : doit être supprimé dès que le background bleu b&you est dispo sur content editor !
        const backgroundColor = this.isFaiPureFiber ? '#2A55A4' : 'unset';
        this.styleOutterDiv = `
        background-image: ${backgroundImage};
        background-size: cover;
        background-color: ${backgroundColor};
    `;
    }
}
